import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CAlert,
  CTable,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPane,
  CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilPeople, 
  cilCart, 
  cilUser, 
  cilLaptop, 
  cilShieldAlt,
  cilReload,
  cilCog // ADDED FOR SERIAL NUMBER CARD
} from '@coreui/icons';
import { superAdminAPI, resellerAPI } from '../services/api';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    resellers: false,
    clients: false,
    orders: false,
    products: false
  });
  const [data, setData] = useState({
    resellers: [],
    clients: [],
    orders: [],
    products: []
  });
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('resellers');
  
  // Track which data has been loaded
  const [loaded, setLoaded] = useState({
    resellers: false,
    clients: false,
    orders: false,
    products: false
  });

  // Fetch resellers data
  const fetchResellers = async () => {
    setLoading(prev => ({ ...prev, resellers: true }));
    setError('');
    try {
      const response = await superAdminAPI.getResellers();
      if (response.data.success) {
        setData(prev => ({ ...prev, resellers: response.data.resellers }));
        setLoaded(prev => ({ ...prev, resellers: true }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch resellers');
    } finally {
      setLoading(prev => ({ ...prev, resellers: false }));
    }
  };

  // Fetch clients data
  const fetchClients = async () => {
    setLoading(prev => ({ ...prev, clients: true }));
    setError('');
    try {
      const response = await superAdminAPI.getClients();
      if (response.data.success) {
        setData(prev => ({ ...prev, clients: response.data.clients }));
        setLoaded(prev => ({ ...prev, clients: true }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch clients');
    } finally {
      setLoading(prev => ({ ...prev, clients: false }));
    }
  };

  // Fetch orders data
  const fetchOrders = async () => {
    setLoading(prev => ({ ...prev, orders: true }));
    setError('');
    try {
      const response = await superAdminAPI.getOrders();
      if (response.data.success) {
        setData(prev => ({ ...prev, orders: response.data.orders }));
        setLoaded(prev => ({ ...prev, orders: true }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch orders');
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  };

  // Fetch products data
  const fetchProducts = async () => {
    setLoading(prev => ({ ...prev, products: true }));
    setError('');
    try {
      const response = await resellerAPI.getAllProducts();
      if (response.data.success) {
        setData(prev => ({ ...prev, products: response.data.products }));
        setLoaded(prev => ({ ...prev, products: true }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch products');
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading({
          resellers: true,
          clients: true,
          orders: true,
          products: true
        });
        
        // Fetch all data in parallel
        await Promise.all([
          fetchResellers(),
          fetchClients(),
          fetchOrders(),
          fetchProducts()
        ]);
      } catch (err) {
        setError('Failed to load dashboard data');
      } finally {
        setLoading({
          resellers: false,
          clients: false,
          orders: false,
          products: false
        });
      }
    };

    fetchAllData();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Fetch data if not already loaded
    if (!loaded[tab]) {
      switch(tab) {
        case 'resellers':
          fetchResellers();
          break;
        case 'clients':
          fetchClients();
          break;
        case 'orders':
          fetchOrders();
          break;
        case 'products':
          fetchProducts();
          break;
        default:
          break;
      }
    }
  };

  const refreshData = () => {
    switch(activeTab) {
      case 'resellers':
        fetchResellers();
        break;
      case 'clients':
        fetchClients();
        break;
      case 'orders':
        fetchOrders();
        break;
      case 'products':
        fetchProducts();
        break;
      default:
        break;
    }
  };

  // Navigation functions
  const navigateToResellers = () => {
    navigate('/super-admin/resellers');
  };

  const navigateToClients = () => {
    navigate('/super-admin/clients');
  };

  const navigateToOrders = () => {
    navigate('/super-admin/orders');
  };

  const navigateToProducts = () => {
    navigate('/software-products');
  };

  // Helper function to safely extract values from MongoDB extended JSON
  const extractValue = (value) => {
    if (!value) return null;
    
    if (typeof value === 'object' && value !== null) {
      if (value.$numberDouble) {
        return value.$numberDouble;
      }
      if (value.$numberInt) {
        return value.$numberInt;
      }
      if (value.$numberLong) {
        return value.$numberLong;
      }
      return Object.values(value)[0] || value;
    }
    
    return value;
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = extractValue(amount);
    if (!num || isNaN(num)) return '$0.00';
    return `$${parseFloat(num).toFixed(2)}`;
  };

  return (
    <div>
      {/* Header with Super Admin Badge */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <CIcon icon={cilShieldAlt} className="me-2 text-danger" />
            Super Admin Dashboard
            <CBadge color="danger" className="ms-2">
              <CIcon icon={cilShieldAlt} className="me-1" />
              Super Admin
            </CBadge>
          </h2>
          <p className="text-medium-emphasis">
            Full system access - View and manage all data
          </p>
        </div>
        <CButton 
          color="danger" 
          onClick={refreshData}
          disabled={loading[activeTab]}
        >
          <CIcon icon={cilReload} className="me-2" />
          Refresh Data
        </CButton>
      </div>

      {/* Error Alert */}
      {error && (
        <CAlert color="danger" className="mb-3">
          {error}
        </CAlert>
      )}

      {/* Stats Cards */}
      <CRow className="g-4 mb-4">
        <CCol md={3}>
          <CCard className="h-100">
            <CCardBody className="text-center">
              <CIcon icon={cilPeople} size="3xl" className="text-primary mb-3" />
              <h5>Resellers</h5>
              <p className="fs-4 fw-bold">{data.resellers.length}</p>
              <CButton 
                color="primary" 
                onClick={navigateToResellers}
              >
                View All Resellers
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
        
        <CCol md={3}>
          <CCard className="h-100">
            <CCardBody className="text-center">
              <CIcon icon={cilUser} size="3xl" className="text-success mb-3" />
              <h5>Clients</h5>
              <p className="fs-4 fw-bold">{data.clients.length}</p>
              <CButton 
                color="success" 
                onClick={navigateToClients}
              >
                View All Clients
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
        
        <CCol md={3}>
          <CCard className="h-100">
            <CCardBody className="text-center">
              <CIcon icon={cilCart} size="3xl" className="text-warning mb-3" />
              <h5>Orders</h5>
              <p className="fs-4 fw-bold">{data.orders.length}</p>
              <CButton 
                color="warning" 
                onClick={navigateToOrders}
              >
                View All Orders
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
        
        <CCol md={3}>
          <CCard className="h-100">
            <CCardBody className="text-center">
              <CIcon icon={cilLaptop} size="3xl" className="text-info mb-3" />
              <h5>Products</h5>
              <p className="fs-4 fw-bold">{data.products.length}</p>
              <CButton 
                color="info" 
                onClick={navigateToProducts}
              >
                View All Products
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>

        {/* NEW: Serial Number Generation Card */}
        <CCol md={3}>
          <CCard className="h-100">
            <CCardBody className="text-center">
              <CIcon icon={cilCog} size="3xl" className="text-dark mb-3" />
              <h5>Serial Numbers</h5>
              <p className="fs-4 fw-bold">Generate</p>
              <CButton 
                color="dark" 
                onClick={() => navigate('/super-admin/serial-number-generation')}
              >
                <CIcon icon={cilCog} className="me-2" />
                Generate Serial Numbers
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Data Tables Tabs */}
      <CTabs activeTab={activeTab} onActiveTabChange={handleTabChange}>
        <CTabList>
          <CTab tab="resellers">
            <CIcon icon={cilPeople} className="me-2" />
            Resellers ({data.resellers.length})
          </CTab>
          <CTab tab="clients">
            <CIcon icon={cilUser} className="me-2" />
            Clients ({data.clients.length})
          </CTab>
          <CTab tab="orders">
            <CIcon icon={cilCart} className="me-2" />
            Orders ({data.orders.length})
          </CTab>
          <CTab tab="products">
            <CIcon icon={cilLaptop} className="me-2" />
            Products ({data.products.length})
          </CTab>
        </CTabList>

        <CTabContent className="mt-3">
          {/* Resellers Tab */}
          <CTabPane tab="resellers" className="p-3">
            {loading.resellers ? (
              <div className="text-center py-5">
                <CSpinner color="primary" />
                <p className="mt-2">Loading resellers...</p>
              </div>
            ) : (
              <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                  <h5>All Resellers ({data.resellers.length})</h5>
                  <CButton color="primary" onClick={navigateToResellers}>
                    View Full Page →
                  </CButton>
                </CCardHeader>
                <CCardBody>
                  {data.resellers.length === 0 ? (
                    <p className="text-center py-4">No resellers found</p>
                  ) : (
                    <CTable striped hover responsive>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Company Name</th>
                          <th>Email</th>
                          <th>Location</th>
                          <th>Contact</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.resellers.slice(0, 5).map(reseller => (
                          <tr key={reseller._id}>
                            <td>{extractValue(reseller.resellerid) || 'N/A'}</td>
                            <td>{extractValue(reseller.resellername) || 'N/A'}</td>
                            <td>{extractValue(reseller.reseller_contact_email) || 'N/A'}</td>
                            <td>
                              {extractValue(reseller.billing_city) || ''}
                              {extractValue(reseller.billing_state) ? `, ${extractValue(reseller.billing_state)}` : ''}
                            </td>
                            <td>{extractValue(reseller.reseller_contact_full_name) || 'N/A'}</td>
                            <td>
                              <CBadge color={extractValue(reseller.custmertype) === 'Distributor' ? 'info' : 'secondary'}>
                                {extractValue(reseller.custmertype) || 'N/A'}
                              </CBadge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </CTable>
                  )}
                  {data.resellers.length > 5 && (
                    <div className="text-center mt-3">
                      <p>Showing 5 of {data.resellers.length} resellers</p>
                      <CButton color="primary" onClick={navigateToResellers}>
                        View All {data.resellers.length} Resellers
                      </CButton>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            )}
          </CTabPane>

          {/* Clients Tab */}
          <CTabPane tab="clients" className="p-3">
            {loading.clients ? (
              <div className="text-center py-5">
                <CSpinner color="success" />
                <p className="mt-2">Loading clients...</p>
              </div>
            ) : (
              <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                  <h5>All Clients ({data.clients.length})</h5>
                  <CButton color="success" onClick={navigateToClients}>
                    View Full Page →
                  </CButton>
                </CCardHeader>
                <CCardBody>
                  {data.clients.length === 0 ? (
                    <p className="text-center py-4">No clients found</p>
                  ) : (
                    <CTable striped hover responsive>
                      <thead>
                        <tr>
                          <th>Client ID</th>
                          <th>Name</th>
                          <th>Reseller</th>
                          <th>Email</th>
                          <th>Location</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.clients.slice(0, 10).map(client => (
                          <tr key={client._id}>
                            <td>
                              <CBadge color="success">
                                {extractValue(client.clientid)?.substring(0, 8) || 'N/A'}...
                              </CBadge>
                            </td>
                            <td>{extractValue(client.name) || 'N/A'}</td>
                            <td>{extractValue(client.resellername) || 'N/A'}</td>
                            <td>{extractValue(client.client_owner_email) || 'N/A'}</td>
                            <td>{extractValue(client.billing_city) || 'N/A'}</td>
                            <td>
                              <CBadge color={extractValue(client.custmertype) === 'Distributor' ? 'info' : 'secondary'}>
                                {extractValue(client.custmertype) || 'N/A'}
                              </CBadge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </CTable>
                  )}
                  {data.clients.length > 10 && (
                    <div className="text-center mt-3">
                      <p>Showing 10 of {data.clients.length} clients</p>
                      <CButton color="success" onClick={navigateToClients}>
                        View All {data.clients.length} Clients
                      </CButton>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            )}
          </CTabPane>

          {/* Orders Tab */}
          <CTabPane tab="orders" className="p-3">
            {loading.orders ? (
              <div className="text-center py-5">
                <CSpinner color="warning" />
                <p className="mt-2">Loading orders...</p>
              </div>
            ) : (
              <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                  <h5>All Orders ({data.orders.length})</h5>
                  <CButton color="warning" onClick={navigateToOrders}>
                    View Full Page →
                  </CButton>
                </CCardHeader>
                <CCardBody>
                  {data.orders.length === 0 ? (
                    <p className="text-center py-4">No orders found</p>
                  ) : (
                    <CTable striped hover responsive>
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Product</th>
                          <th>Client</th>
                          <th>Reseller</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.orders.slice(0, 10).map(order => (
                          <tr key={order._id}>
                            <td>#{extractValue(order.subscription_number) || 'N/A'}</td>
                            <td>{extractValue(order.product_name) || 'N/A'}</td>
                            <td>{extractValue(order.account_name) || 'N/A'}</td>
                            <td>{extractValue(order.reseller_name) || 'N/A'}</td>
                            <td className="fw-bold">
                              {formatCurrency(extractValue(order.retail_subscription_price))}
                            </td>
                            <td>
                              <CBadge color="success">
                                {extractValue(order.status) || 'active'}
                              </CBadge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </CTable>
                  )}
                  {data.orders.length > 10 && (
                    <div className="text-center mt-3">
                      <p>Showing 10 of {data.orders.length} orders</p>
                      <CButton color="warning" onClick={navigateToOrders}>
                        View All {data.orders.length} Orders
                      </CButton>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            )}
          </CTabPane>

          {/* Products Tab */}
          <CTabPane tab="products" className="p-3">
            {loading.products ? (
              <div className="text-center py-5">
                <CSpinner color="info" />
                <p className="mt-2">Loading products...</p>
              </div>
            ) : (
              <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                  <h5>All Software Products ({data.products.length})</h5>
                  <CButton color="info" onClick={navigateToProducts}>
                    View Full Page →
                  </CButton>
                </CCardHeader>
                <CCardBody>
                  {data.products.length === 0 ? (
                    <p className="text-center py-4">No products found</p>
                  ) : (
                    <CTable striped hover responsive>
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Description</th>
                          <th>Category</th>
                          <th>Price</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.products.slice(0, 10).map(product => (
                          <tr key={product._id}>
                            <td>{extractValue(product.product_name) || 'N/A'}</td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                {extractValue(product.description) || 'No description'}
                              </div>
                            </td>
                            <td>{extractValue(product.category) || 'N/A'}</td>
                            <td className="fw-bold">
                              {formatCurrency(extractValue(product.price))}
                            </td>
                            <td>
                              <CBadge color="success">
                                {extractValue(product.status) || 'active'}
                              </CBadge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </CTable>
                  )}
                  {data.products.length > 10 && (
                    <div className="text-center mt-3">
                      <p>Showing 10 of {data.products.length} products</p>
                      <CButton color="info" onClick={navigateToProducts}>
                        View All {data.products.length} Products
                      </CButton>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            )}
          </CTabPane>
        </CTabContent>
      </CTabs>
    </div>
  );
};

export default SuperAdminDashboard;